import { Grid, useTheme } from "@mui/material";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxMainTextField from "components/UI/AcxMainTextField";
import React from "react";
import { FormFieldErrors } from "shared/types/FormErrors.type";
import { Organization } from "../../types/Organization.type";
import { kboUserAgreementMaxVersionLength } from "../../kboUserAgreementConfiguration";

/**
 * The KBo User Agreement hub users in this organization must accept before using
 * KBo chat. Leaving every field blank means there is no agreement.
 */
const KboUserAgreementSettings = ({
    isLoading,
    org,
    updateOrg,
    fieldErrors,
}: {
    isLoading: boolean;
    org: Organization | null | undefined;
    updateOrg: (val: string, prop: keyof Organization) => void;
    fieldErrors: FormFieldErrors | undefined;
}) => {
    const theme = useTheme();

    return (
        <>
            <Grid item xs={12} sx={{ mt: 2 }}>
                <AcxInputLabel
                    sx={{ color: theme.palette.black.main }}
                    labelText="KBo User Agreement"
                    infoText="Hub users must accept this before using KBo chat. Write {{OrganizationName}} where the organization's name goes. Changing the text asks everyone to accept again. Leave every field blank to have no agreement."
                />
            </Grid>
            <Grid item xs={4}>
                <AcxMainTextField
                    skeleton={isLoading}
                    onChange={(e) =>
                        updateOrg(e.target.value, "kboUserAgreementVersion")
                    }
                    id="kbo-user-agreement-version"
                    labelText="Version"
                    placeholderText="e.g. 1.0"
                    value={org?.kboUserAgreementVersion}
                    error={!!fieldErrors?.kboUserAgreementVersion}
                    helperText={
                        fieldErrors?.kboUserAgreementVersion ??
                        `Up to ${kboUserAgreementMaxVersionLength} characters`
                    }
                    showAllErrors={true}
                />
            </Grid>
            <Grid item xs={4}>
                <AcxMainTextField
                    skeleton={isLoading}
                    onChange={(e) =>
                        updateOrg(e.target.value, "kboUserAgreementTitle")
                    }
                    id="kbo-user-agreement-title"
                    labelText="Title"
                    placeholderText="KBo User Agreement"
                    value={org?.kboUserAgreementTitle}
                    showAllErrors={true}
                />
            </Grid>
            <Grid item xs={4}>
                <AcxMainTextField
                    skeleton={isLoading}
                    onChange={(e) =>
                        updateOrg(e.target.value, "kboUserAgreementEffectiveOn")
                    }
                    type="date"
                    id="kbo-user-agreement-effective-on"
                    labelText="Effective date"
                    value={org?.kboUserAgreementEffectiveOn}
                    error={!!fieldErrors?.kboUserAgreementEffectiveOn}
                    helperText={fieldErrors?.kboUserAgreementEffectiveOn}
                    showAllErrors={true}
                />
            </Grid>
            <Grid item xs={12}>
                <AcxMainTextField
                    skeleton={isLoading}
                    onChange={(e) =>
                        updateOrg(e.target.value, "kboUserAgreementContent")
                    }
                    id="kbo-user-agreement-content"
                    labelText="Agreement text (markdown)"
                    placeholderText="## 1. What KBo is…"
                    value={org?.kboUserAgreementContent}
                    error={!!fieldErrors?.kboUserAgreementContent}
                    helperText={fieldErrors?.kboUserAgreementContent}
                    showAllErrors={true}
                    multiline={true}
                    rows={15}
                />
            </Grid>
        </>
    );
};

export default KboUserAgreementSettings;
